import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Building2, CreditCard, Users, Settings, ArrowRight, Key, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Lot } from '../contexts/LotContext';

export default function OnboardingComplete() {
  const { lotId } = useParams<{ lotId: string }>();
  const [lot, setLot] = useState<Lot | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if this is a Stripe-only completion vs full onboarding
  const isStripeCompletion = window.location.pathname.includes('/stripe-complete/');

  useEffect(() => {
    const fetchLot = async () => {
      if (!lotId) return;

      try {
        // Use conditional API endpoint for localhost vs production
        const apiUrl = window.location.hostname === 'localhost' 
          ? `/api/complete-onboarding/${lotId}`
          : `/.netlify/functions/complete-onboarding/${lotId}`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to complete onboarding');
        }

        const data = await response.json();
        setLot(data.lot);
        toast.success('Onboarding completed successfully!');
      } catch (error) {
        console.error('Error completing onboarding:', error);
        toast.error('Failed to complete onboarding');
      } finally {
        setLoading(false);
      }
    };

    fetchLot();
  }, [lotId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing setup...</p>
        </div>
      </div>
    );
  }

  if (!lot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Setup Not Complete</h2>
            <p className="text-gray-600 mb-4">
              We couldn't find your lot information. Please try the setup process again.
            </p>
            <Link
              to="/signup/lot-owner"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Over
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Success Header */}
          <div className="px-6 py-8 bg-green-600 text-white text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold">
              {isStripeCompletion ? 'Payment Setup Complete!' : 'Congratulations!'}
            </h1>
            <p className="text-green-100 mt-2">
              {isStripeCompletion 
                ? 'Your Stripe Connect account is now set up and ready to receive payments' 
                : 'Your parking lot is now live and ready to accept payments'
              }
            </p>
          </div>

          <div className="px-6 py-8">
            {/* Lot Information */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Building2 className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Your Parking Lot</h2>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900">{lot.display_name}</h3>
                <p className="text-blue-700 text-sm mt-1">Slug: {lot.slug}</p>
                <p className="text-blue-700 text-sm">Status: <span className="font-medium">Active & Accepting Payments</span></p>
              </div>
            </div>

            {/* Login Instructions - only show for new onboarding */}
            {!isStripeCompletion && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Key className="h-6 w-6 text-purple-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Access Your Dashboard</h2>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-purple-800 mb-3">
                  <strong>Important:</strong> A user account has been created for you with your email address.
                </p>
                <div className="space-y-2 text-purple-700">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2" />
                    <span className="text-sm">Check your email for login credentials</span>
                  </div>
                  <p className="text-sm">
                    You should receive an email shortly with your temporary password. 
                    You can change this password after logging in.
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* Quick Access */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Access</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/admin"
                  className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group"
                >
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="font-medium text-blue-900">Admin Dashboard</h3>
                    <p className="text-blue-700 text-sm">Manage parking sessions and view analytics</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </Link>

                <Link
                  to={`/lots/${lot.slug}`}
                  className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group"
                >
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="font-medium text-green-900">Public Lot Page</h3>
                    <p className="text-green-700 text-sm">Customer parking booking page</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Next Steps</h2>
              <div className="space-y-4">
                {isStripeCompletion ? (
                  // Stripe completion steps
                  <>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-white text-sm font-bold">1</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Payments Are Now Active</h3>
                        <p className="text-gray-600 text-sm">Your lot can now accept payments directly to your bank account</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-white text-sm font-bold">2</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Review Your Settings</h3>
                        <p className="text-gray-600 text-sm">Double-check your lot settings and pricing in the admin dashboard</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-white text-sm font-bold">3</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Monitor Your Dashboard</h3>
                        <p className="text-gray-600 text-sm">Keep track of payments and analytics through Stripe and your admin panel</p>
                      </div>
                    </div>
                  </>
                ) : (
                  // Full onboarding steps
                  <>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Check Your Email</h3>
                    <p className="text-gray-600 text-sm">Look for your login credentials in your inbox</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Log In to Your Dashboard</h3>
                    <p className="text-gray-600 text-sm">Access your admin dashboard to manage settings and view analytics</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Share Your Lot</h3>
                    <p className="text-gray-600 text-sm">Share your public lot URL with customers: 
                      <br />
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs mt-1 inline-block">
                        {window.location.origin}/lots/{lot.slug}
                      </code>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Set Up Your Team</h3>
                    <p className="text-gray-600 text-sm">Add team members to help manage your parking lot</p>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>

            {/* Support Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-600 text-sm mb-2">
                If you have any questions or need assistance, please don't hesitate to reach out:
              </p>
                             <ul className="text-gray-600 text-sm space-y-1">
                <li>• Email: support@parkinglittleitaly.com</li>
                <li>• Platform Fee: 10% on all parking sessions</li>
                <li>• Payments go directly to your bank account</li>
              </ul>
            </div>

            {/* Get Started Button */}
            <div className="mt-8 text-center">
              <Link
                to={isStripeCompletion ? `/admin/lots/${lot.slug}/settings` : "/admin"}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Users className="h-5 w-5 mr-2" />
                {isStripeCompletion ? 'Back to Settings' : 'Go to Admin Dashboard'}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 